import { useEffect, useState, useRef } from 'react';
import { Formik, Form } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { updateSubscription } from '../../reducers/subscription';
import { createSubscription } from '../../services/subscription';
import { useStripe, useElements } from '@stripe/react-stripe-js';

import verifyUser from '../../services/verifyUser';
import { trackPageView, trackGaEvent } from '../../services/tracking';
import FormErrors from '../formErrors';
import { isValid } from 'creditcard.js';
import { validateExpiryDate, validateName } from '../../utils/validators';
import { cpf } from 'cpf-cnpj-validator';

import { Row, Col } from '../grid';
import { Button } from '../button';
import { Input, } from '../input';
import { FormActions } from '../formActions';
import { PageHeader } from '../pageHeader';
import PaymentFields from './paymentFields';
import Summary from '../summary';

import { StyledTerms } from './style';

export const PaymentForm = () => {
  const formikRef = useRef();
  const dispatch = useDispatch();

  const stripe = useStripe();
  const elements = useElements();

  const subscription = useSelector((state) => state.subscription);
  const initialValues = { ...subscription };

  const [formErrors, setFormErrors] = useState([]);

  const validate = (values) => {
    let errors = {};

    if (!values.name){
      errors.name = 'Digite o seu nome completo';
    }
    else if(!validateName(values.name)){
      errors.name = 'Digite um nome válido';
    }

    if(!values.email){
      errors.email = 'Digite o seu email';
    }
    else if(!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)){
      errors.email = 'Digite um email válido';
    }

    if(!values.password){
      errors.password = 'Digite a sua senha';
    }
    if(values.passwordConfirmation){
      if(values.password !== values.passwordConfirmation){
        errors.passwordConfirmation = 'Confirmação de senha é diferente da senha escolhida';
      }
    }
    else {
      errors.passwordConfirmation = 'Confirme a sua senha';
    }

    if(process.env.REACT_APP_PAYMENT_GATEWAY === 'stripe'){
      if(!window['$']('#card-number-element')[0].classList.contains('StripeElement--complete')){
        errors.creditCardNumber = 'Digite um número de cartão de crédito válido';
      }

      if(!window['$']('#card-expiry-element')[0].classList.contains('StripeElement--complete')){
        errors.creditCardExpiry = 'Data inválida';
      }

      if(!window['$']('#card-cvc-element')[0].classList.contains('StripeElement--complete')){
        errors.creditCardCvc = 'CVC inválido';
      }
    }
    else {
      if(values.creditCardNumber){
        if(!isValid(values.creditCardNumber)){
          errors.creditCardNumber = 'O número do cartão de crédito é inválido';
        }
      }
      else {
        errors.creditCardNumber = 'Digite o número do seu cartão de crédito';
      }

      if(values.creditCardExpiry){
        if(!validateExpiryDate(values.creditCardExpiry)){
          errors.creditCardExpiry = 'Data inválida';
        }
      }
      else {
        errors.creditCardExpiry = 'Digite a data de expiração';
      }

      if(process.env.REACT_APP_PAYMENT_GATEWAY === 'mercadopago'){
        if(!values.creditCardCvc){
          errors.creditCardCvc = 'CVC inválido';
        }
      }
    }

    if(!values.creditCardHolderName){
      errors.creditCardHolderName = 'Digite o nome impresso no seu cartão';
    }

    if(!values.cpf){
      errors.cpf = 'Digite o seu CPF';
    }
    else if(!cpf.isValid(values.cpf)){
      errors.cpf = 'Digite um CPF válido';
    }

    return errors;
  }

  const handleSubmit = (values) => {
    dispatch(updateSubscription({
      name: values.name,
      email: values.email,
      password: values.password,
      passwordConfirmation: values.passwordConfirmation,

      creditCardNumber: values.creditCardNumber,
      creditCardHolderName: values.creditCardHolderName,
      creditCardExpiry: values.creditCardExpiry,
      creditCardCvc: values.creditCardCvc,
      cpf: values.cpf,
    }));

    createSubscription({ setFormErrors, stripe, elements });

    trackGaEvent('add_payment_info');
  }

  const handleCpfBlur = (e) => {
    verifyCpf(e.target.value);
  }

  const verifyCpf = (cpf) => {
    if(cpf) verifyUser({ cpf });
  }

  const handleEmailBlur = (e) => {
    verifyEmail(e.target.value);
  }

  const verifyEmail = (email) => {
    if(email) verifyUser({ email });
  }

  useEffect(() => {
    verifyCpf(subscription.cpf);

    trackPageView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={validate}
      >
        {({ setFieldValue, values, errors, setFieldError }) => (
          <Form>
            <PageHeader>
              <small>Pagamento</small>
              <h1>Você está quase lá!</h1>
              <p>Só nos informar os dados que processaremos o pagamento para você.</p>
            </PageHeader>

            <FormErrors errors={formErrors} />

            <Input name="name" label="Nome completo" />

            <Input
              name="email"
              label="Seu email"
              type="email"
              autoComplete="username"
              onBlur={handleEmailBlur}
            />

            <Input
              name="cpf"
              format="###.###.###-##"
              label="CPF"
              onBlur={handleCpfBlur}
            />

            <Row>
              <Col>
                <Input
                  name="password"
                  type="password"
                  label="Senha"
                  autoComplete="new-password"
                />
              </Col>

              <Col>
                <Input
                  name="passwordConfirmation"
                  type="password"
                  label="Confirme a senha"
                  autoComplete="new-password"
                />
              </Col>
            </Row>

            <div className="sep"></div>

            <PaymentFields errors={errors} setFieldError={setFieldError} />

            <Summary />

            <FormActions columns="one">
              <Button type="submit" buttonStyle="full">
                Finalizar e criar minha assinatura
              </Button>

              <small>Pause, pule o envio ou cancele quando quiser</small>
            </FormActions>
          </Form>
        )}
      </Formik>

      <StyledTerms>
        Ao clicar em "Finalizar", você estará concordando com os nossos <a href={`${process.env.REACT_APP_API_BASE_URL}/terms`} target="_blank" rel="noreferrer">termos de uso</a>
      </StyledTerms>
    </div>
  )
}

export default PaymentForm;
